import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import {
	createSubscribeCheckout,
	createSubscribeCheckoutWithTrial,
	fetchAvailablePlans,
	fetchCurrentSubscription,
} from 'data/reducers/subscriptionReducer';
import { SubscriptionHelper } from 'view/helpers';
import { FREE_TRIAL_CONFIG } from 'constants/freeTrialConfig';

// SMS Instant Alerts rides on monthly or yearly base plans using a matching-interval
// Stripe price ($19.99/mo or $214.99/yr with 10% yearly discount).
export const SMS_ADDON_MONTHLY_PRICE = 19.99;
export const SMS_ADDON_YEARLY_PRICE = 214.99;
const MONTHS_PER_YEAR = 12;

/** Amount Stripe collects at checkout: full annual price on yearly, monthly price on monthly. */
export const getAmountDueAtCheckout = (plan, isYearly) => {
	const amount = Number(isYearly ? plan?.yearlyPrice : plan?.monthlyPrice);
	return Number.isFinite(amount) ? amount : 0;
};

/** Per-month equivalent of the selected interval, exact to the cent. */
export const getMonthlyEquivalentPrice = (plan, isYearly) => {
	const amount = getAmountDueAtCheckout(plan, isYearly);
	return isYearly ? amount / MONTHS_PER_YEAR : amount;
};

/**
 * SMS add-on amount for the selected billing period: $214.99/yr or $19.99/mo.
 */
export const getSmsAddonAmountForInterval = (isYearly) =>
	isYearly ? SMS_ADDON_YEARLY_PRICE : SMS_ADDON_MONTHLY_PRICE;

/**
 * Pricing / checkout behaviour for the marketing Pricing page (VWA-2898).
 *
 * This is the logic of the previous landing page's PricingTable
 * (view/pages/landingPage/pricingTable/PricingTable.jsx) - same Redux actions, same plan
 * lookup, same button states, same login redirect and pending-checkout replay - so the
 * redesigned page buys exactly what the old one did. Only the markup differs.
 */
export default function usePricingCheckout() {
	const [isYearly, setIsYearly] = useState(true);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedModalPlan, setSelectedModalPlan] = useState(null);
	const [modalWithSms, setModalWithSms] = useState(false);
	const [isProcessingModalCheckout, setIsProcessingModalCheckout] = useState(false);

	const includeSmsAddon = modalWithSms;

	const dispatch = useDispatch();
	const history = useHistory();

	const { plans, isCreatingCheckout, checkoutError } = useSelector(
		(state) => state.pricing
	);
	const { authToken } = useSelector((state) => state.user);
	const {
		availablePlans,
		currentSubscription,
		hasSubscription,
		isCreatingSubscribeCheckout,
		subscribeCheckoutError,
	} = useSelector((state) => state.subscription);
	const isLoggedIn = !!authToken;

	// Plans are publicly available, so they are fetched even when logged out
	useEffect(() => {
		if (availablePlans.length === 0) {
			dispatch(fetchAvailablePlans());
		}
	}, [dispatch, availablePlans.length]);

	useEffect(() => {
		if (isLoggedIn && !currentSubscription) {
			dispatch(fetchCurrentSubscription());
		}
	}, [dispatch, isLoggedIn, currentSubscription]);

	// The .mk-root layout is the scroll container, so the modal locks that instead of <body>
	// (the app shell already keeps <body> from scrolling).
	useEffect(() => {
		const handleKeyDown = (e) => {
			if (e.key === 'Escape' && isModalOpen && !isProcessingModalCheckout) {
				setIsModalOpen(false);
			}
		};
		if (isModalOpen) {
			window.addEventListener('keydown', handleKeyDown);
		}
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [isModalOpen, isProcessingModalCheckout]);

	const dynamicPlans = useMemo(
		() => SubscriptionHelper.combineSubscriptionPlans(availablePlans),
		[availablePlans]
	);
	const finalPlans = dynamicPlans.length > 0 ? dynamicPlans : plans;

	const subscriptionOptions = {
		isLoggedIn,
		hasSubscription,
		currentSubscription,
		availablePlans,
		isProcessing: isCreatingCheckout || isCreatingSubscribeCheckout,
	};

	const getCurrentPlan = () =>
		SubscriptionHelper.getCurrentSubscriptionPlan(
			currentSubscription,
			availablePlans,
			hasSubscription
		);
	const getButtonText = (plan) =>
		SubscriptionHelper.getPlanButtonText(plan, subscriptionOptions);
	const getPlanButtonDisabled = (plan) =>
		SubscriptionHelper.isPlanButtonDisabled(plan, subscriptionOptions);
	const isCurrentPlan = (plan) =>
		!!(
			hasSubscription &&
			currentSubscription &&
			getCurrentPlan() &&
			SubscriptionHelper.extractPlanType(getCurrentPlan().name) === plan.id
		);

	// Core checkout dispatch to Stripe
	const handleStripeCheckout = useCallback(
		async (plan, withTrial = false, withSmsAddon = false) => {
			try {
				const databasePlanId = SubscriptionHelper.getPlanDatabaseId(plan, isYearly);
				if (!databasePlanId) {
					return;
				}

				if (withTrial) {
					await dispatch(
						createSubscribeCheckoutWithTrial(
							databasePlanId,
							FREE_TRIAL_CONFIG.DEFAULT_DAYS,
							FREE_TRIAL_CONFIG.DEFAULT_SOURCE,
							withSmsAddon
						)
					);
				} else {
					await dispatch(
						createSubscribeCheckout(databasePlanId, withSmsAddon)
					);
				}
			} catch (error) {
				let errorMessage =
					'There was an error processing your request. Please try again.';

				if (error.type === 'invalid_request_error') {
					errorMessage = 'Invalid request. Please contact support.';
				} else if (error.type === 'api_connection_error') {
					errorMessage =
						'Connection error. Please check your internet and try again.';
				} else if (error.type === 'api_error') {
					errorMessage = 'Service temporarily unavailable. Please try again.';
				} else if (error.type === 'authentication_error') {
					errorMessage = 'Authentication error. Please contact support.';
				}

				alert(errorMessage);
			}
		},
		[dispatch, isYearly]
	);

	// Plan button click
	const handlePlanSelect = async (plan) => {
		if (plan.isCustom) {
			window.location.href =
				'mailto:admin@violerts.com?subject=Enterprise Plan Inquiry';
			return;
		}

		if (
			SubscriptionHelper.shouldManagePlanInSettings(
				currentSubscription,
				hasSubscription
			)
		) {
			history.push('/dashboard/settings');
			return;
		}

		if (plan.id === 'free') {
			if (!isLoggedIn) {
				history.push('/sign-up');
				return;
			}
			history.push('/dashboard');
			return;
		}

		// Paid plans open the pre-checkout customization modal
		setSelectedModalPlan(plan);
		setModalWithSms(false);
		setIsModalOpen(true);
	};

	// Checkout confirmation from the modal
	const handleModalCheckout = async (forceIncludeSms = null) => {
		if (!selectedModalPlan) return;
		const finalSmsState =
			forceIncludeSms !== null ? forceIncludeSms : modalWithSms;

		if (!isLoggedIn) {
			// Remember the selection and replay it after sign-up / login
			SubscriptionHelper.storePendingCheckout(
				selectedModalPlan,
				isYearly,
				false,
				finalSmsState
			);
			history.push('/sign-up?redirect=checkout');
			setIsModalOpen(false);
			return;
		}

		setIsProcessingModalCheckout(true);
		try {
			await handleStripeCheckout(selectedModalPlan, false, finalSmsState);
		} finally {
			setIsProcessingModalCheckout(false);
			setIsModalOpen(false);
		}
	};

	// Fallback replay if auth landed on pricing without starting Stripe directly
	useEffect(() => {
		if (isLoggedIn) {
			const planData = SubscriptionHelper.getPendingCheckout();
			if (planData) {
				const plan = finalPlans.find((p) => p.id === planData.planId);
				if (plan && planData.planId) {
					setIsYearly(planData.billingInterval === 'yearly');

					SubscriptionHelper.clearPendingCheckout();
					handleStripeCheckout(
						plan,
						planData.withTrial || false,
						planData.withSmsAddon || false
					);
				}
			}
		}
	}, [isLoggedIn, finalPlans, handleStripeCheckout]);

	return {
		isYearly,
		setIsYearly,
		finalPlans,
		hasSubscription,
		currentSubscription,
		getButtonText,
		getPlanButtonDisabled,
		isCurrentPlan,
		handlePlanSelect,
		checkoutError: checkoutError || subscribeCheckoutError,
		modal: {
			isOpen: isModalOpen,
			plan: selectedModalPlan,
			includeSmsAddon,
			toggleSms: () => setModalWithSms(!modalWithSms),
			isProcessing: isProcessingModalCheckout,
			close: () => !isProcessingModalCheckout && setIsModalOpen(false),
			checkout: () => handleModalCheckout(),
		},
	};
}
